import { useEffect, useState } from 'react';
import { ReviewModal } from '@kw/quokka-ui';
import { useSnackbar } from 'notistack';
import { sendHttpRequest } from '../../../utils/network.service';

export function GuardianReviewModal({
  mode,
  setMode,
  selectedItem,
  setLoading,
  fetchData,
  reviewType,
  afterSaveArgs = [],
  analysisId,
}: {
  mode: 'single' | 'all' | null;
  setMode: (mode: 'single' | 'all' | null) => void;
  selectedItem: any;
  setLoading?: (loading: boolean) => void;
  fetchData?: (...args: any[]) => Promise<void>;
  reviewType: 'rule' | 'regulation';
  afterSaveArgs?: any[];
  analysisId: string;
}) {
  const [singleOpen, setSingleOpen] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const reviewTypeMapping = {
    rule: {
      id: selectedItem?.guid,
      name:
        selectedItem?.rule?.name ?? selectedItem?.rule?.shortDescription?.text ?? selectedItem?.rule?.fullDescription?.text?.split('.')[0],
      metadata: selectedItem?.metadata ?? [],
      label: 'Rule',
    },
    regulation: {
      id: selectedItem?.regulationControlId,
      name: selectedItem?.regulationControlId,
      metadata: selectedItem?.regulationControlMetadata ?? [],
      label: 'Regulation',
    },
  };

  const stop = e => e?.stopPropagation?.();

  const toggleSingle = (e?: React.MouseEvent) => {
    stop(e);
    setSingleOpen(open => {
      if (open) setMode?.(null);
      return !open;
    });
  };

  const toggleAll = (e?: React.MouseEvent) => {
    stop(e);
    setAllOpen(open => {
      if (open) setMode?.(null);
      return !open;
    });
  };

  // Build review data from adapters
  const { id, name, metadata, label } = reviewTypeMapping[reviewType];

  const future = metadata.find(m => m?.appliesToFutureVersions);
  const current = metadata.find(m => !m?.appliesToFutureVersions);

  const futureReviewData = {
    ...future,
    id,
    name,
  };

  const currentReviewData = {
    ...current,
    id,
    name,
  };

  const modeMapping = {
    single: { isOpen: singleOpen, toggle: toggleSingle, reviewData: currentReviewData },
    all: { isOpen: allOpen, toggle: toggleAll, reviewData: futureReviewData },
  };

  useEffect(() => {
    if (mode && modeMapping[mode] && !modeMapping[mode].isOpen) {
      modeMapping[mode].toggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleSaveReview = async params => {
    setLoading?.(true);
    try {
      await sendHttpRequest({
        path: `${process.env.EVAL_SERVICE_URL}/eval/review-metadata/${analysisId}`,
        method: 'POST',
        body: {
          reviewType,
          reviewId: params.id,
          reviewStatus: params.reviewStatus,
          note: params.note,
          appliesToFutureVersions: params.appliesToFutureVersions,
        },
        successMessage: 'Successfully updated review',
        errorMessage: 'Error updating review',
        enqueueSnackbar,
      });
    } finally {
      await fetchData?.(...afterSaveArgs);
      setLoading?.(false);
    }
  };

  if (!mode) return null;

  return (
    <ReviewModal
      type={mode}
      isOpen={modeMapping[mode].isOpen}
      toggle={modeMapping[mode].toggle}
      reviewItem={label}
      reviewData={modeMapping[mode].reviewData}
      saveReviewCallback={handleSaveReview}
    />
  );
}
